/**
 * 放置全局一些通用配置
 */
export const DEFAULT_DATA = "--";

const PROCESS_ENV_NODE: "development" | "production" | "test" | undefined =
  process.env.NODE_ENV;

export const DOMAIN_NAME: Record<string, Record<string, string>> = {
  appStore: {
    dev: "https://devstore.kaihong.com",
    test: "https://teststore.kaihong.com",
    prod: "http://10.70.1.209",
  },
};

/** 完整应用市场基地址，优先于域名推断与子域前缀，如 https://teststore.kaihong.com */
const APP_STORE_BASE_URL_ENV = "/api/app-store";

const getAppStoreUrlFromEnv = (): string | undefined => {
  if (APP_STORE_BASE_URL_ENV) {
    return APP_STORE_BASE_URL_ENV.replace(/\/+$/, "");
  }
  return undefined;
};

export const getBaseUrl = (projectName: string) => {
  if (projectName === "appStore") {
    const fromEnv = getAppStoreUrlFromEnv();
    if (fromEnv) {
      return fromEnv;
    }
  }

  const origin = window.location.origin;
  if (PROCESS_ENV_NODE === "development") {
    // 本地环境
    return DOMAIN_NAME[projectName].dev;
  } else {
    if (origin.indexOf("//dev") !== -1) {
      // 开发环境
      return DOMAIN_NAME[projectName].dev;
    } else if (origin.indexOf("//test") !== -1) {
      // 测试环境
      return DOMAIN_NAME[projectName].test;
    } else {
      // 生产环境
      return DOMAIN_NAME[projectName].prod;
    }
  }
};

const urlToAuthorizationMatch = (url: string) => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

const appStoreUrlForAuth = getAppStoreUrlFromEnv();

/**
 * 需携带与 X-Access-Token 一致的 Authorization 头的请求 URL 匹配项
 * 请求 URL 中包含数组中任一项即注入 Authorization（通常为 域名:端口 或 完整 host）
 * 包含 DOMAIN_NAME.appStore 各环境，以及环境变量覆盖时的 host，避免漏配
 */
export const AUTH_AUTHORIZATION_URLS: string[] = Array.from(
  new Set(
    [
      ...Object.values(DOMAIN_NAME.appStore),
      ...(appStoreUrlForAuth ? [appStoreUrlForAuth] : []),
    ].map(urlToAuthorizationMatch)
  )
);

// 如果此处修改了，请同步修改src\assets\style\theme.less中的@ant-prefix
export const ANT_PREFIX = "khAnt";

// hotfix-28822: 工作台存在业务规则告警记录卡片(需要隐藏)，与原型图不符
// 定义需要隐藏的菜单Code列表
export const HIDDEN_MENU_CODES = [
  "rule-engine/Alarm/Log",
  "/operation-management/business",
];

export const HIDDEN_MENU_CODES_KEY = "HIDDEN_MENU_CODES";
export const SYSTEM_CONFIG_FRONT_KEY = "SYSTEM_CONFIG_FRONT";

const getHiddenMenuCodes = () => {
  const systemConfigFront =
    localStorage.getItem(SYSTEM_CONFIG_FRONT_KEY) || "{}";
  try {
    const parsed = JSON.parse(systemConfigFront);
    const dynamicHiddenCodes = Array.isArray(parsed?.menuHiddenCodes)
      ? parsed.menuHiddenCodes
      : [];
    return Array.from(new Set([...HIDDEN_MENU_CODES, ...dynamicHiddenCodes]));
  } catch (error) {
    console.error(error);
    return HIDDEN_MENU_CODES;
  }
};

// 定义树节点接口
interface TreeNode {
  code: string;
  children?: TreeNode[];
  [key: string]: any; // 允许其他任意属性
}

// 定义过滤函数类型
type TreeFilterFunction = (
  treeData: TreeNode | TreeNode[],
  hiddenCodes?: string[]
) => TreeNode[] | TreeNode | null;

/**
 * 过滤树节点，移除需隐藏的节点及其子节点
 * @param treeData - 树结构数据（支持数组或单个节点对象）
 * @param hiddenCodes - 需要隐藏的节点code数组
 * @returns 过滤后的树结构（可能为空数组/null/过滤后的节点）
 */
export const filterHiddenMenuCodes: TreeFilterFunction = (
  treeData,
  hiddenCodes = getHiddenMenuCodes()
) => {
  // 创建Set提高查找效率
  const hiddenSet = new Set(hiddenCodes);

  // 提取以 /* 结尾的 code 作为前缀匹配规则
  const hiddenPrefixes = Array.from(hiddenSet)
    .filter((code) => code.endsWith("/*"))
    .map((code) => code.replace("*", ""));

  // 内部递归过滤函数
  const filterNode = (node: TreeNode): TreeNode | null => {
    // 1. 如果当前节点需要隐藏，直接返回null（整个子树移除）
    if (hiddenSet.has(node.code)) {
      return null;
    }

    // 2. 如果当前节点的 code 匹配任何隐藏前缀，也返回null
    if (hiddenPrefixes.some((prefix) => node.code.startsWith(prefix))) {
      return null;
    }

    // 3. 深度优先处理子节点（如果有）
    const filteredChildren: TreeNode[] = [];
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        const filteredChild = filterNode(child);
        if (filteredChild) {
          filteredChildren.push(filteredChild);
        }
      }
    }

    // 4. 返回节点副本（避免修改原数据）并更新子节点
    return {
      ...node,
      children: filteredChildren.length > 0 ? filteredChildren : undefined,
    };
  };

  // 处理数组形式的树结构（多根节点）
  if (Array.isArray(treeData)) {
    return treeData
      .map((node) => filterNode(node))
      .filter(Boolean) as TreeNode[];
  }
  // 处理对象形式的单根节点
  else if (treeData && typeof treeData === "object") {
    return filterNode(treeData as TreeNode);
  }

  // 空数据直接返回
  return treeData;
};
